import { Component, Inject, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { ReportExportViewService } from '../../../../report-export-view.service';
import { ReportRecord } from '../../../../models/report.model';
import {
  AddAdditionalImageDialogComponent,
  AdditionalImageDialogInputData,
  genThumbMediaURI,
  isMediaInfoPublishable,
  PropertyMediaInfo,
} from '@miq/utils';
import { L10nTranslationService } from 'angular-l10n';
import { REPORT_EXPORT_CONFIGS_TOKEN, ReportExportConfigurations } from '../../../../report-export-view.token';
import { NotificationService } from '../../../../shared/services/notification.service';
import { filter, switchMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { ReportImagesService } from '../../../../report-images.service';
import { ReportAllRecordsImage } from '../../../../models/template.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AdditionalImagesPosition } from '../../../../shared/templates/config/common';
import { Router } from '@angular/router';

@UntilDestroy()
@Component({
  // eslint-disable-next-line
  selector: 'floor-plan',
  templateUrl: './floor-plan.component.html',
  styleUrls: ['./floor-plan.component.scss'],
  standalone: false,
})
export class FloorPlanComponent implements OnInit, OnDestroy, OnChanges {
  @Input() reportId: number;
  @Input() reportRecordData: ReportRecord;
  @Input() templateId: number;

  public availabilityInfo = [];
  public edpMultimediaURI: string;
  public s3BucketImagesUrl: string;
  public selectedImageId: number;
  public reportAllImages: ReportAllRecordsImage;
  public floorPlanImages = [];

  constructor(
    @Inject(REPORT_EXPORT_CONFIGS_TOKEN) reportModuleConfigs: ReportExportConfigurations,
    private reportExportViewService: ReportExportViewService,
    public translationService: L10nTranslationService,
    private notificationService: NotificationService,
    private reportImagesService: ReportImagesService,
    private router: Router
  ) {
    this.edpMultimediaURI = reportModuleConfigs.edpMultimediaURI;
    this.s3BucketImagesUrl = reportModuleConfigs.s3BucketImagesUrl;
  }

  ngOnChanges() {
    if (this.reportRecordData && this.reportRecordData.data) {
      this.availabilityInfo = Array.isArray(this.reportRecordData.data)
        ? (this.reportRecordData.data as [])
        : [this.reportRecordData.data];
    }
    this.showFloorPlanImages();
  }

  ngOnInit() {
    this.reportImagesService.onUpdateAdditionalImage$.pipe(untilDestroyed(this)).subscribe(response => {
      this.reportAllImages = response;
      this.showFloorPlanImages();
    });
  }

  ngOnDestroy() {
    // used for destroy the Subscription.
  }

  public openDialog(availabilityId: number, method: 'add' | 'edit') {
    const availabilityInfo = this.availabilityInfo.find(availability => availability.entity.id == availabilityId);
    const imagesToDisplay: PropertyMediaInfo[] = this.getFloorPlanImages(availabilityInfo);
    const options = { height: '80%', width: '80%', position: { top: '30px' } };
    const translatedData = {
      close: this.translationService.translate('core.close'),
      selectAttachment: this.translationService.translate('core.selectAttachment'),
      select: this.translationService.translate('recordsClipboard.select'),
      noImagesFound: this.translationService.translate('core.noImagesFound'),
      navigationMessage: this.translationService.translate('core.navigationMessage'),
      addNew: this.translationService.translate('recordsSearch.addNew'),
    };
    const data: AdditionalImageDialogInputData = {
      imagesToDisplay,
      multiSelect: false,
      edpMultimediaURI: this.edpMultimediaURI,
      s3BucketImagesUrl: this.s3BucketImagesUrl,
      selectedImageId: this.reportAllImages[availabilityInfo.entity.id]
        ? this.reportAllImages[availabilityInfo.entity.id][0].imageId
        : null,
      translatedData,
      navigationMessage: true,
      availabilityId,
    };
    let selectedImage: PropertyMediaInfo;
    this.notificationService
      .openDialog<PropertyMediaInfo>(AddAdditionalImageDialogComponent, data, options)
      .pipe(
        filter(image => image != null),
        switchMap((image: PropertyMediaInfo) => {
          selectedImage = image;
          let addEdit$: Observable<any> = of();
          if (method === 'add') {
            addEdit$ = this.reportExportViewService.addAdditionalImage(this.reportId, [
              { edpRecordId: availabilityInfo.entity.id, imageId: image.id },
            ]);
          } else {
            const reportImageId =
              this.reportAllImages[availabilityInfo.entity.id][AdditionalImagesPosition.AdditionalImage].reportImageId;
            addEdit$ = this.reportExportViewService.changeAdditionalImage(this.reportId, reportImageId, image.id);
          }
          return addEdit$;
        })
      )
      .subscribe((repImageId: number | number[]) => {
        const reportImageId = Array.isArray(repImageId) ? repImageId[0] : repImageId;
        if (method === 'add') {
          this.reportAllImages[availabilityInfo.entity.id] = [
            {
              reportImageId,
              imageId: selectedImage.id,
            },
          ];
        } else {
          this.reportAllImages[availabilityInfo.entity.id][0] = {
            reportImageId,
            imageId: selectedImage.id,
          };
        }
        this.reportImagesService.setReportRecordImages(this.reportAllImages);
      });
  }

  public deleteFloorPlan(floorPlanInfo: { id: number }) {
    const reportImageId = this.reportAllImages[floorPlanInfo.id][0].reportImageId;
    this.reportExportViewService.deleteAdditionalImage(this.reportId, reportImageId).subscribe(() => {
      delete this.reportAllImages[floorPlanInfo.id];
      this.reportImagesService.setReportRecordImages(this.reportAllImages);
      this.showFloorPlanImages();
    });
  }

  public navigate(availabilityId: number) {
    this.router.navigateByUrl(`/records/availability/${availabilityId}/edit`);
  }

  private getFloorPlanImages(availability) {
    let imagesToDisplay: PropertyMediaInfo[] = [];
    const mediaInfo = availability.getMediaInformation();
    if (mediaInfo.length) {
      imagesToDisplay = mediaInfo
        .filter(media => media.media_content_type_desc === 'Floor Plan' && media.publish_image_f)
        .sort((a, b) => a.display_order - b.display_order);
    }
    return imagesToDisplay;
  }

  private showFloorPlanImages() {
    this.floorPlanImages = [];
    if (this.availabilityInfo.length) {
      this.availabilityInfo.forEach((availability, index) => {
        const availabilityMediaInfo = availability.getMediaInformation();
        const selectedMedia = (availabilityMediaInfo || []).find(media => {
          let imageInfo =
            this.reportAllImages && this.reportAllImages[availability.entity.id]
              ? this.reportAllImages[availability.entity.id].find(image => image.imageId === media.id)
              : null;
          return imageInfo ? imageInfo : null;
        });
        let data;
        if (selectedMedia) {
          data = {
            id: availability.entity.id,
            position: index + 1,
            imageSrc: genThumbMediaURI(this.edpMultimediaURI, this.s3BucketImagesUrl, selectedMedia.media_path, {
              provideDefault: true,
            }),
            publishable: isMediaInfoPublishable(availability),
            floor_suite: {
              floor_number: availability.floor_suite.floor_number,
              suite_number: availability.floor_suite.suite_number,
            },
          };
        } else {
          const availabilityImages = availabilityMediaInfo.filter(
            media => media.media_content_type_desc === 'Floor Plan' && media.publish_image_f
          );
          data = {
            id: availability.entity.id,
            position: index + 1,
            availabilityImages: availabilityImages.length,
            floor_suite: {
              floor_number: availability.floor_suite.floor_number,
              suite_number: availability.floor_suite.suite_number,
            },
          };
        }
        this.floorPlanImages.push(data);
      });
    }
  }
}
